import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { Address } from 'types/api/address';

import { useI18n } from 'lib/i18n/context';
import TokenEntity from 'ui/shared/entities/token/TokenEntity';
import Hint from 'ui/shared/Hint';

import { ADDRESS_DETAILS_KEYS } from '../i18n';

interface Props {
  data: Pick<Address, 'name' | 'token' | 'is_contract'>;
  isLoading: boolean;
}

const AddressNameInfo = ({ data, isLoading }: Props) => {
  const { t } = useI18n();
  const primaryText = useColorModeValue('#000000', '#FFFFFF');
  const secondaryText = useColorModeValue('#616161', '#999999');

  const labelSx = {
    fontFamily: '\'Space Grotesk\', var(--chakra-fonts-body)',
    fontWeight: 500,
    fontSize: '14px',
    color: secondaryText,
    textTransform: 'uppercase' as const,
  };

  const valueSx = {
    fontFamily: '\'Oxanium\', var(--chakra-fonts-body)',
    fontWeight: 500,
    fontSize: '16px',
    color: primaryText,
  };

  if (data.token) {
    return (
      <Box alignSelf="stretch">
        <Flex alignItems="center" gap="4px" mb="4px">
          <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.tokenName) }</Text>
          <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.tokenName) } isLoading={ isLoading } iconColor={ secondaryText }/>
        </Flex>
        <Box { ...valueSx }>
          <TokenEntity
            token={ data.token }
            isLoading={ isLoading }
            noIcon
            noCopy
          />
        </Box>
      </Box>
    );
  }

  if (data.is_contract && data.name) {
    return (
      <Box alignSelf="stretch">
        <Flex alignItems="center" gap="4px" mb="4px">
          <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.contractName) }</Text>
          <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.contractName) } isLoading={ isLoading } iconColor={ secondaryText }/>
        </Flex>
        <Box { ...valueSx }>
          <Text>{ data.name }</Text>
        </Box>
      </Box>
    );
  }

  if (data.name) {
    return (
      <Box alignSelf="stretch">
        <Flex alignItems="center" gap="4px" mb="4px">
          <Text { ...labelSx }>{ t(ADDRESS_DETAILS_KEYS.fields.validatorName) }</Text>
          <Hint label={ t(ADDRESS_DETAILS_KEYS.tooltip.validatorName) } isLoading={ isLoading } iconColor={ secondaryText }/>
        </Flex>
        <Box { ...valueSx }>
          <Text>{ data.name }</Text>
        </Box>
      </Box>
    );
  }

  return null;
};

export default React.memo(AddressNameInfo);
